import React, {useContext, useState} from "react";
import {
  Box, CircularProgress, Collapse, FormControl, FormControlLabel, Grid, IconButton, InputAdornment,
  makeStyles, Radio, RadioGroup, Typography,
} from "@material-ui/core";
import {FormattedMessage, useIntl} from "react-intl";
import CloseIcon from "@material-ui/icons/Close";
import moment from "moment";

import {DefaultButton, OutlineButton} from "./Buttons";
import {AppScrollbar} from "../../../components/scrollbar/AppScrollbar";
import {NotificationWrapper} from "./GenericWrapper";
import {NotificationDispatchContext} from "../../../context/NotificationContext";
import ICIPAdminService from "../../../services/ICIPAdminService";
import {InputDate} from "../../../components/input-date/InputDate";
import {IoIosCalendar} from "react-icons/io";
import {YYYY_MM_DD_FORMAT} from "../../../settings/constants";

export type ExportTag = 'all' | 'confirmed' | 'unconfirmed';
export type PeriodTag = 'all' | 'specific';

const initialPeriods = (started: boolean = false) => {
  return {
    start_date: started ? moment().format(YYYY_MM_DD_FORMAT) : null,
    end_date: started ? moment().format(YYYY_MM_DD_FORMAT) : null,
  };
};

export interface ExportRaffleParticipantsWrapperProps {
  onClose: () => void;
}

export const useStyles = makeStyles(
  (theme) => ({
    root: {
      overflow: "hidden"
    },
    headerWrapper: {},
    header: {
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(2),
      paddingTop: theme.spacing(2),
    },
    footerWrapper: {
      display: "flex",
      alignItems: "center",
      height: "80px",

      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(4),
      borderTop: "1px solid #e8e8e8"
    },
    footerExtra: {
      flexGrow: 1,
      textAlign: "start",
    },
    footerActions: {
      display: "flex",
      alignItems: "center",
    },
    contentWrapper: {
      height: "calc(100vh - 150px)",
    },
    wrapperItem: {
      padding: "4px 26px"
    },
    wrapperItemTitle: {
      textAlign: "left",
      fontSize: "0.88rem",
      color: theme.palette.grey[600]
    },
    wrapperItemContent: {},
  }),
  {
    name: "export-wrapper-style",
    flip: null,
  }
);

enum ExportStatus {
  loading,
  failure,
  succeed,
  initial,
}

const ExportRaffleParticipantsWrapper = (props: ExportRaffleParticipantsWrapperProps) => {
  const classes = useStyles(props);
  const intl = useIntl();
  const {onClose} = props;
  // Context
  const dispatch = useContext(NotificationDispatchContext);
  // State
  const [exportStatus, setExportStatus] = useState(
      (): ExportStatus => ExportStatus.initial
  );
  const [periodTag, setPeriodTag] = useState(
      (): PeriodTag => "all"
  );
  const [periods, setPeriods] = useState(initialPeriods(false));

  const handlePeriodChange = (event: any) => {
    setPeriodTag(event.target.value);
    if(event.target.value === "all") {
      setPeriods(initialPeriods(false));
    } else {
      setPeriods(initialPeriods(true));
    }
  };

  const handleDateChange = (name: string, dateString: string) => {
    setPeriods({ ...periods, [name]: dateString });
  };

  const exportRequest = (tag: ExportTag) => {
    setExportStatus(ExportStatus.loading);

    const response = ICIPAdminService.raffleDrawParticipationResumeExportation(tag, periods);
    response.then((response) => {
      if (response.isSuccess && response.data.code === 100) {
        let apiResponse = JSON.parse(JSON.stringify(response.data.data));

        window.open(apiResponse.link, "_blank");
        setExportStatus(ExportStatus.succeed);
      } else {
        setExportStatus(ExportStatus.failure);
        dispatch({
          data: {
            type: "error",
            message: intl.formatMessage({ id: "sharedScreen.unableToExportData" }),
          }
        });
      }
    });
  };

  return (
      <Box className={classes.root}>
        <NotificationWrapper />
        <form noValidate autoComplete="off">
          <Box component="div" className={classes.headerWrapper}>
            <Box component="div" className={classes.header}>
              <Box display="flex" alignItems="center">
                <Box style={{ flexGrow: 1 }}>
                  <Typography component="span" style={{fontWeight: 600, fontSize: "1.2rem"}}>
                    <FormattedMessage id="sharedScreen.exportRaffleParticipants" />
                  </Typography>
                </Box>
                <IconButton href="" aria-label="close" onClick={(_) => onClose()} disabled={exportStatus === ExportStatus.loading}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Box>
          <AppScrollbar>
            <Box component="div" className={classes.contentWrapper} pb={6}>
              <Box component="div" mt={2} style={{ paddingBottom: "4rem" }}>
                <Grid container component="div" className={classes.wrapperItem}>
                  <Grid item sm={12} component="div" className={classes.wrapperItemContent}>
                    <Box>
                      <Typography component="p" className={classes.wrapperItemTitle}>
                        <FormattedMessage id="sharedScreen.periods" />
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center">
                      <FormControl component="fieldset">
                        <RadioGroup
                            row
                            aria-label="period"
                            name="period_filters"
                            defaultValue={periodTag}
                            onChange={handlePeriodChange}
                        >
                          <FormControlLabel value="all" control={<Radio size="small" />} label={intl.formatMessage({id: "sharedScreen.allPeriods"})} />
                          <FormControlLabel value="specific" control={<Radio size="small" />} label={intl.formatMessage({id: "sharedScreen.specificPeriods"})} />
                        </RadioGroup>
                      </FormControl>
                    </Box>
                    <Collapse in={periodTag === "specific"}  timeout="auto" unmountOnExit>
                      <Box display="flex" alignItems="center">
                        <Box component="div">
                          <InputDate
                              maxDate=""
                              onDateChange={(name, date) => handleDateChange(name, date)}
                              inputProps={{
                                fullWidth: true,
                                id: "start_date",
                                name: "start_date",
                                placeholder: intl.formatMessage({
                                  id: "sharedScreen.startDate",
                                }),
                                endAdornment: (
                                    <InputAdornment position="end">
                                      <IoIosCalendar />
                                    </InputAdornment>
                                ),
                                value: periods.start_date,
                              }}
                              pickerProps={{
                                format: YYYY_MM_DD_FORMAT,
                                disabled: false,
                                showToday: false,
                              }}
                          />
                        </Box>
                        <Box component="div" pl={1} >
                          <InputDate
                              maxDate=""
                              onDateChange={(name, date) => handleDateChange(name, date)}
                              inputProps={{
                                fullWidth: true,
                                id: "end_date",
                                name: "end_date",
                                placeholder: intl.formatMessage({
                                  id: "sharedScreen.endDate",
                                }),
                                endAdornment: (
                                    <InputAdornment position="end">
                                      <IoIosCalendar />
                                    </InputAdornment>
                                ),

                                value: periods.end_date,
                              }}
                              pickerProps={{
                                format: YYYY_MM_DD_FORMAT,
                                disabled: false,
                                showToday: false,
                              }}
                          />
                        </Box>
                      </Box>
                    </Collapse>
                  </Grid>
                </Grid>

                <Grid container component="div" className={classes.wrapperItem}>
                  <Grid item sm={12} component="div" className={classes.wrapperItemContent}>
                    <Box>
                      <Typography component="p" className={classes.wrapperItemTitle}>
                        <FormattedMessage id="sharedScreen.allParticipants" />
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" mt={1} mb={2}>
                      <DefaultButton
                          disabled={exportStatus === ExportStatus.loading}
                          onClick={(_) => exportRequest('all')}
                      >
                        <FormattedMessage id="sharedScreen.excelFile" />
                      </DefaultButton>
                    </Box>
                  </Grid>
                </Grid>
                <Grid container component="div" className={classes.wrapperItem}>
                  <Grid item sm={12} component="div" className={classes.wrapperItemContent}>
                    <Box>
                      <Typography component="p" className={classes.wrapperItemTitle}>
                        <FormattedMessage id="sharedScreen.confirmedParticipants" />
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" mt={1} mb={2}>
                      <OutlineButton
                          disabled={exportStatus === ExportStatus.loading}
                          onClick={(_) => exportRequest('confirmed')}
                      >
                        <FormattedMessage id="sharedScreen.excelFile" />
                      </OutlineButton>
                    </Box>
                  </Grid>
                </Grid>
                <Grid container component="div" className={classes.wrapperItem}>
                  <Grid item sm={12} component="div" className={classes.wrapperItemContent}>
                    <Box>
                      <Typography component="p" className={classes.wrapperItemTitle}>
                        <FormattedMessage id="sharedScreen.unConfirmedParticipants" />
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" mt={1} mb={2}>
                      <OutlineButton
                          disabled={exportStatus === ExportStatus.loading}
                          onClick={(_) => exportRequest('unconfirmed')}
                      >
                        <FormattedMessage id="sharedScreen.excelFile" />
                      </OutlineButton>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </AppScrollbar>
          <Box component="div" className={classes.footerWrapper}>
            <Box component="div" className={classes.footerExtra}>
              {
                exportStatus === ExportStatus.loading ? (
                    <CircularProgress color="secondary" disableShrink={false} size={20} thickness={4} />
                ) : (<></>)
              }
            </Box>
            <Box component="div" className={classes.footerActions}>
              <Box component="div">
                <OutlineButton onClick={(_) => onClose()} disabled={exportStatus === ExportStatus.loading}>
                  <FormattedMessage id="sharedScreen.cancel" />
                </OutlineButton>
              </Box>
            </Box>
          </Box>
        </form>
      </Box>
  );
};

export default ExportRaffleParticipantsWrapper;